import React, { useState, useEffect, useRef } from 'react';
import AddIcon from '@mui/icons-material/Add';

const OperationAuthAutocomplete = ({
  value,
  onChange,
  options,
  label,
  onAddNew,
  placeholder = 'Operasyon yetkilisi ara...',
  error = false,
  errorMessage = '',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState([]);
  const wrapperRef = useRef(null);

  // Initialize the input value based on the selected value
  useEffect(() => {
    if (value) {
      // Convert value to string for comparison since IDs from database might be numbers
      const valueStr = String(value);
      const selectedOption = options.find(
        (option) => String(option.id) === valueStr
      );
      if (selectedOption) {
        setInputValue(`${selectedOption.name} ${selectedOption.surname}`);
      } else {
        setInputValue('');
      }
    } else {
      setInputValue('');
    }
  }, [value, options]);

  // Filter options based on input value
  useEffect(() => {
    if (options && options.length > 0) {
      const filtered = options.filter((option) => {
        const fullName = `${option.name} ${option.surname}`.toLowerCase();
        return (
          fullName.includes(inputValue.toLowerCase()) ||
          (option.phone && option.phone.includes(inputValue))
        );
      });
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions([]);
    }
  }, [inputValue, options]);

  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setIsOpen(true);

    // If input is cleared, also clear the selected value
    if (!e.target.value) {
      onChange('');
    }
  };

  const handleOptionClick = (option) => {
    // Ensure we're passing the ID in the same format it was received
    onChange(option.id);
    setInputValue(`${option.name} ${option.surname}`);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {error && <span className="text-red-500">*</span>}
      </label>
      <div className="flex space-x-2">
        <div className="relative flex-1">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {isOpen && filteredOptions.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {filteredOptions.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleOptionClick(option)}
                  className="px-4 py-2 hover:bg-blue-50 cursor-pointer"
                >
                  <div className="font-medium">
                    {option.name} {option.surname}
                  </div>
                  <div className="text-sm text-gray-500">
                    {option.phone && `Tel: ${option.phone}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {onAddNew && (
          <button
            type="button"
            onClick={onAddNew}
            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <AddIcon className="w-4 h-4 mr-1" />
            Yetkili Ekle
          </button>
        )}
      </div>
      {error && errorMessage && (
        <p className="error-message text-red-500 text-xs mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

export default OperationAuthAutocomplete;
